import {
  AddressFormRow,
  FormGroup,
  FormInput,
  FormSelect,
  StyledPhone,
} from '@Components/AddressForm/AddressForm.styled';
import { CountryCode, ErrorMessage, FieldWrapper } from '@Components/Auth/Auth.styled';
import { Spinner } from '@Components/common/Spinner';
import { useAuth } from '@Contexts/AuthContext/Auth.context';
import { _CreatePickupItem } from '@Services/Checkout';
import { CountryCode as CountryCodeType } from 'libphonenumber-js/types';
import useTranslation from 'next-translate/useTranslation';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { UnmountClosed } from 'react-collapse';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { PickupCity, PickupPoint } from 'types/Checkout';
import { PickupItemParams } from 'types/Checkout.d';
import { SelectOption } from 'types/common';
import PickupPointCard from '../PickupPointCard';
import {
  BranchesList,
  BranchesSection,
  PickupFormWrapper,
  PickupListWrapper,
  PickupMapWrapper,
  SavePickupPointBtn,
  SubCollapseTitle,
} from './CheckoutSteps.styled';

type Props = {
  pickupCities?: PickupCity[];
  pickupPoints?: PickupPoint[];
  handleSelectPickupPoint: (pickupPointId: any) => Promise<void>;
  setPickupDistrictId: React.Dispatch<React.SetStateAction<number | undefined>>;
  pickupIsLoading: boolean;
};
export default function AddressCollapsePickup({
  pickupCities,
  pickupPoints,
  handleSelectPickupPoint,
  setPickupDistrictId,
  pickupIsLoading,
}: Props) {
  const { t } = useTranslation('Checkout');
  const { data } = useSelector((state: RootState) => state.checkout);
  const [selectedCity, setSelectedCity] = useState<SelectOption | null>();
  const [selectedDistrict, setSelectedDistrict] = useState<SelectOption | null>();
  const citiesOptions = pickupCities?.map((city) => ({
    label: city.city,
    value: city.city,
  }));
  const [pickupPointId, setPickupPointId] = useState<number | undefined>();
  const { userData } = useAuth();
  const getDistricts = useCallback(
    (city: SelectOption) => {
      return pickupCities
        ?.find((c) => c.city === city.value)
        ?.districts.map((district) => ({
          label: district.name,
          value: district.id,
        }));
    },
    [pickupCities]
  );

  const {
    register,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PickupItemParams>();
  const [country, setCountry] = useState<CountryCodeType | undefined>('EG');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (pickupPointId) {
      setValue('pick_up_point', pickupPointId);
    }
  }, [pickupPointId, setValue]);

  useEffect(() => {
    const parsePhone = parsePhoneNumber(userData?.mobile as string);
    setValue('mobile', parsePhone?.number as string);
    setValue('full_name', userData?.full_name as string);
    setValue('email', userData?.email as string);
    setTimeout(() => {
      setCountry(parsePhone?.country);
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const onSavePickup: SubmitHandler<PickupItemParams> = (data) => {
    setIsLoading(true);
    _CreatePickupItem(data)
      .then(async (data) => {
        await handleSelectPickupPoint(data.id);
        setIsLoading(false);
        setPickupPointId(undefined);
      })
      .catch((e) => {
        console.log(e);
        setIsLoading(false);
      });
  };

  return (
    <Fragment>
      <PickupListWrapper>
        <BranchesSection>
          <SubCollapseTitle>{t('choose_center')}</SubCollapseTitle>
          <BranchesList>
            {pickupPoints ? (
              pickupPoints?.map((pickup) => (
                <PickupPointCard
                  key={pickup.id}
                  data={pickup}
                  onClick={setPickupPointId}
                  isSelected={data?.pick_point?.pick_up_point.id === pickup.id || pickupPointId === pickup.id}
                  isDisabled={pickupIsLoading}
                />
              ))
            ) : (
              <Spinner />
            )}
          </BranchesList>
        </BranchesSection>
        <PickupMapWrapper>
          <FormSelect
            onChange={(value: any, triggeredAction: any) => {
              setSelectedDistrict(null);
              setPickupDistrictId(undefined);
              if (triggeredAction.action === 'clear') {
                setSelectedCity(null);
              } else {
                setSelectedCity(value as SelectOption);
              }
            }}
            isSearchable={true}
            isClearable={true}
            value={selectedCity}
            options={citiesOptions}
            placeholder={t('select-province')}
            isDisabled={!citiesOptions}
          />
          <FormSelect
            onChange={(value: any) => {
              setPickupDistrictId((value as SelectOption).value);
              setSelectedDistrict(value as SelectOption);
            }}
            isSearchable={true}
            isClearable={false}
            value={selectedDistrict}
            options={selectedCity ? getDistricts(selectedCity) : undefined}
            placeholder={t('select-district')}
            isDisabled={!citiesOptions || !selectedCity}
          />
        </PickupMapWrapper>
      </PickupListWrapper>
      <UnmountClosed isOpened={Boolean(pickupPointId)}>
        <PickupFormWrapper onSubmit={handleSubmit(onSavePickup)}>
          <AddressFormRow>
            <FormGroup>
              <FormInput
                type="text"
                placeholder={t('enter-your-name')}
                {...register('full_name', {
                  required: true,
                })}
                disabled={isLoading}
              />
              {errors?.full_name && (
                <ErrorMessage>
                  {errors.full_name?.type === 'required' ? t('enter-your-name') : errors.full_name?.message}
                </ErrorMessage>
              )}
            </FormGroup>
          </AddressFormRow>
          <AddressFormRow>
            <FormInput type="email" placeholder={t('enter-your-email')} {...register('email')} disabled={isLoading} />
            {errors?.email && (
              <ErrorMessage>
                {errors.email?.type === 'required' ? t('enter-email') : errors.email?.message}
              </ErrorMessage>
            )}
          </AddressFormRow>
          <AddressFormRow>
            <FieldWrapper>
              <CountryCode>+{country ? getCountryCallingCode(country) : ''}</CountryCode>
              <Controller
                name="mobile"
                control={control}
                rules={{
                  validate: (value) => (value ? isValidPhoneNumber(value.toString()) : true),
                }}
                render={({ field: { onChange, value } }) => (
                  <StyledPhone
                    value={value?.toString()}
                    onChange={(v: any) => onChange(v || null)}
                    placeholder={t('enter-phone-number')}
                    defaultCountry={country}
                    smartCaret={false}
                    onCountryChange={(country?: CountryCodeType) => setCountry(country)}
                    disabled={isLoading}
                    id="PHONE"
                  />
                )}
              />
              {Boolean(errors.mobile) && <ErrorMessage>{t('phone_error_validate')}</ErrorMessage>}
            </FieldWrapper>
          </AddressFormRow>
          <AddressFormRow>
            <SavePickupPointBtn variant="outlined" type="submit">
              Save Pickup point
            </SavePickupPointBtn>
          </AddressFormRow>
        </PickupFormWrapper>
      </UnmountClosed>
    </Fragment>
  );
}
